// src/services/graphService.ts

import { Client } from "@microsoft/microsoft-graph-client";
import "isomorphic-fetch";
import { IPublicClientApplication } from "@azure/msal-browser";

const GRAPH_SCOPES = ["User.Read", "GroupMember.Read.All"];

/**
 * Gets user groups from Microsoft Graph API.
 * Filters to only return Microsoft Graph group objects (excludes directory roles).
 * 
 * @param {IPublicClientApplication} msalInstance - MSAL application instance
 * @returns {Promise<Array<{id: string; name: string}>>} Array of group objects with id and name
 * @throws {Error} If no signed-in account found or API call fails
 * 
 * @example
 * ```tsx
 * const groups = await getUserGroups(msalApp);
 * // Returns: [{ id: 'group-id-1', name: 'Group Name 1' }, ...]
 * ```
 */
export async function getUserGroups(msalInstance: IPublicClientApplication) {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error("No signed-in account found");
  }

  const tokenResponse = await msalInstance.acquireTokenSilent({
    scopes: GRAPH_SCOPES,
    account: accounts[0]
  });

  const client = Client.init({
    authProvider: (done) => {
      done(null, tokenResponse.accessToken);
    }
  });

  let allGroups: any[] = [];
  let nextLink: string | undefined = "/me/memberOf";

  while (nextLink) {
    const response = await client.api(nextLink).get();
    allGroups = allGroups.concat(response.value);
    nextLink = response["@odata.nextLink"];
  }
  return allGroups
    .filter((obj: any) => obj["@odata.type"] === "#microsoft.graph.group")
    .map((g: any) => ({
      id: g.id,
      name: g.displayName
    }));
}

/**
 * Gets user profile photo from Microsoft Graph API.
 * Returns base64-encoded image data URL or null if no photo exists.
 * 
 * @param {IPublicClientApplication} msalInstance - MSAL application instance
 * @returns {Promise<string | null>} Base64 data URL of profile photo or null if no photo
 * @throws {Error} If no signed-in account found or API call fails (except 404)
 * 
 * @example
 * ```tsx
 * const photo = await getUserProfilePhoto(msalApp);
 * // Returns: 'data:image/jpeg;base64,...' or null
 * ```
 */
export async function getUserProfilePhoto(msalInstance: IPublicClientApplication) {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error("No signed-in account found");
  }

  const tokenResponse = await msalInstance.acquireTokenSilent({
    scopes: GRAPH_SCOPES,
    account: accounts[0]
  });

  const client = Client.init({
    authProvider: done => done(null, tokenResponse.accessToken)
  });

  try {
    // Profile photo is returned as a binary stream
    const photoBlob = await client
      .api("/me/photo/$value")
      .get();

    // Convert blob to base64
    const base64 = await blobToBase64(photoBlob);
      
    return `data:image/jpeg;base64,${base64}`;

  } catch (err: any) {
    // If the user doesn't have a profile picture, Graph returns 404
    if (err.statusCode === 404) {
      return null; // No profile picture
    }
    throw err;
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(",")[1];
      resolve(base64);
    };
    reader.readAsDataURL(blob);
  });
}